import { keepPreviousData } from '@tanstack/react-query';
import { array } from 'fp-ts';
import {
    Table,
    TableActiveColumnFilters,
    TableBulkActions,
    TableHeader,
    TableProvider,
    TableState,
    useRowSelection,
} from '@optimus-web/core';
import { LocalizedPageTitle, PortfolioLayout, PortfolioLayoutHeader } from '@mews-commander/ui';
import { LocalizedText, useUrlSyncedTableState } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    PortfolioEnterpriseRelationshipFilters,
    PortfolioEnterpriseRelationshipResult,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { WithGuideLink } from '@mews-commander/types';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { CreateRateDialog } from '../../../enterpriseRateManagement/components/sections/components';
import { AddPropertiesGroup } from '../../PropertyGroups';
import { TableHeaderRowActions } from '../../components/RowActions/TableHeaderRowActions';
import { CreateRateGroupDialog } from '../../../enterpriseRateManagement/components/sections/components/rateGroup/CreateRateGroupDialog';
import { useColumns } from './tableMapping';

export const PropertiesTable = ({ guideLink }: WithGuideLink) => {
    const { rowSelection, setRowSelection, selectedRowsIds, hasRowsSelected } = useRowSelection();

    const columns = useColumns();

    const [tableState, setTableState] = useUrlSyncedTableState<
        PortfolioEnterpriseRelationshipResult,
        PortfolioEnterpriseRelationshipFilters
    >({ columns, pageSize: 50 });

    const { data, isFetching } = usePropertiesData(tableState);

    const totalRowCount = data?.totalRowCount || 0;

    return (
        <PortfolioLayout>
            <PortfolioLayoutHeader
                title={<LocalizedPageTitle textKey={TextKey.PropertiesNavigation} />}
                guideLink={guideLink}
            />
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <TableHeader
                    title={<LocalizedText textKey={TextKey.PropertiesNavigation} />}
                    subTitle={totalRowCount}
                />

                <TableActiveColumnFilters />

                <TableBulkActions
                    disabled={!hasRowsSelected}
                    leftSide={
                        array.isNonEmpty(selectedRowsIds) && (
                            <LocalizedText
                                textKey={TextKey.SelectedItems}
                                substitutes={{ count: selectedRowsIds.length }}
                            />
                        )
                    }
                    rightSide={
                        <>
                            <CreateRateDialog enterpriseIds={selectedRowsIds} />

                            <CreateRateGroupDialog enterpriseIds={selectedRowsIds} />

                            <AddPropertiesGroup enterpriseIds={selectedRowsIds} />

                            <TableHeaderRowActions enterpriseIds={selectedRowsIds} />
                        </>
                    }
                />

                <Table
                    data={data?.data ?? []}
                    amountOfRows={totalRowCount}
                    loading={isFetching}
                    rowSelection={{ rowSelection, onRowSelectionChange: setRowSelection }}
                    uniqueRowId="id"
                    fixedFirstColumn
                    fixedLastColumn
                />
            </TableProvider>
        </PortfolioLayout>
    );
};
const usePropertiesData = (
    tableState: TableState<PortfolioEnterpriseRelationshipResult, PortfolioEnterpriseRelationshipFilters>
) => {
    const enterpriseId = useCurrentEnterpriseId();

    return useRepositoryQuery(
        'portfolioEnterpriseRelationships',
        'getAll',
        { enterpriseId, ...tableState },
        { placeholderData: keepPreviousData }
    );
};
